import { Polyline, CircleMarker, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';

import {
  useAppSelector,
  routeResultSelector,
  showCustomRouteSelector,
  customRouteStartSelector,
  customRouteFromWaypointSelector,
  fromWaypointNodeSelector,
  routeEndpointsSelector,
} from '../../../store';

import './CustomRouteDisplay.scss';

// Custom "You Are Here" icon
const youAreHereIcon = L.divIcon({
  className: 'you-are-here-marker',
  html: `
    <div class="you-are-here-pulse"></div>
    <div class="you-are-here-dot"></div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

/**
 * Displays the calculated custom route on the map.
 * Uses the internal pathfinding algorithm instead of MapBox.
 * Supports both space-to-space routes and waypoint-to-space routes.
 */
export function CustomRouteDisplay() {
  const showRoute = useAppSelector(showCustomRouteSelector);
  const startSpaceId = useAppSelector(customRouteStartSelector);
  const fromWaypointId = useAppSelector(customRouteFromWaypointSelector);
  const fromWaypointNode = useAppSelector(fromWaypointNodeSelector);
  const endpoints = useAppSelector(routeEndpointsSelector);

  const routeResult = useAppSelector(routeResultSelector);
  const isWaypointRoute = !!fromWaypointId;

  const startName = isWaypointRoute
    ? (fromWaypointNode?.name || 'Current Location')
    : (endpoints.find((e) => e.id === startSpaceId)?.name || 'Start');

  // Get waypoint position for "You Are Here" marker
  const waypointPosition: [number, number] | null = fromWaypointNode
    ? [fromWaypointNode.position.x, fromWaypointNode.position.y]
    : null;

  // Show "You Are Here" marker whenever we have a waypoint, even without a route
  const showYouAreHereMarker = fromWaypointId && waypointPosition;

  // Convert positions to Leaflet format [lat, lng]
  const positions: [number, number][] = routeResult?.positions?.map((p) => [p.x, p.y]) || [];

  // If no route to display, just show "You Are Here" marker
  if (!showRoute || !routeResult?.found || !routeResult.positions) {
    return showYouAreHereMarker ? (
      <Marker
        position={waypointPosition}
        icon={youAreHereIcon}
        zIndexOffset={1000}
      >
        <Tooltip permanent direction="top" offset={[0, -20]} className="you-are-here-tooltip">
          {startName}
        </Tooltip>
      </Marker>
    ) : null;
  }

  return (
    <>
      {/* "You Are Here" marker for waypoint routes */}
      {showYouAreHereMarker && (
        <Marker
          position={waypointPosition}
          icon={youAreHereIcon}
          zIndexOffset={1000}
        >
          <Tooltip permanent direction="top" offset={[0, -20]} className="you-are-here-tooltip">
            {startName}
          </Tooltip>
        </Marker>
      )}

      {/* Route line - animated dashed blue line */}
      <Polyline
        positions={positions}
        pathOptions={{
          color: '#2196F3',
          weight: 5,
          opacity: 0.9,
          lineCap: 'round',
          lineJoin: 'round',
          dashArray: '12, 8',
        }}
        className="animated-route-line"
      />

      {/* Start marker - only show for space-to-space routes (waypoint routes use "You Are Here" marker) */}
      {!isWaypointRoute && positions.length > 0 && (
        <CircleMarker
          center={positions[0]}
          radius={10}
          pathOptions={{
            fillColor: '#4CAF50',
            fillOpacity: 1,
            color: '#2E7D32',
            weight: 3,
          }}
        />
      )}

      {/* End marker */}
      {positions.length > 1 && (
        <CircleMarker
          center={positions[positions.length - 1]}
          radius={10}
          pathOptions={{
            fillColor: '#F44336',
            fillOpacity: 1,
            color: '#C62828',
            weight: 3,
          }}
        />
      )}

      {/* Intermediate waypoints */}
      {positions.slice(1, -1).map((pos, idx) => (
        <CircleMarker
          key={idx}
          center={pos}
          radius={6}
          pathOptions={{
            fillColor: '#2196F3',
            fillOpacity: 0.8,
            color: '#1565C0',
            weight: 2,
          }}
        />
      ))}
    </>
  );
}
